import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/BranchDayStatus.jsx
// Shows every branch's business day status for the selected date.
// HO: sees all branches in a table with status + cash summary.
// Branch users: see their own branch as a single detail card.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Typography, Spin, Alert,
  Row, Col, Statistic, DatePicker, Tooltip, Badge, Divider,
} from 'antd';
import {
  ReloadOutlined, CalendarOutlined, CheckCircleOutlined,
  ClockCircleOutlined, CloseCircleOutlined, MinusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById } from '../../branches/api';
import { fetchDayStatus } from '../api';
import { DAY_STATUS } from '../constants/treasuryConstants';

const { Title, Text } = Typography;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const STATUS_CONFIG = {
  NO_DAY:                    { color: 'default', label: 'No Activity',              icon: <MinusCircleOutlined /> },
  [DAY_STATUS.OPEN]:                    { color: 'green',   label: 'Open',                   icon: <CheckCircleOutlined /> },
  [DAY_STATUS.PENDING_CASH_VERIFICATION]: { color: 'orange', label: 'Pending Verification',  icon: <ClockCircleOutlined /> },
  [DAY_STATUS.PENDING_CHECKER_APPROVAL]:  { color: 'blue',   label: 'Pending Approval',      icon: <ClockCircleOutlined /> },
  [DAY_STATUS.CLOSED]:                  { color: 'default', label: 'Closed',                 icon: <CloseCircleOutlined /> },
  [DAY_STATUS.REOPENED]:                { color: 'purple',  label: 'Reopened',               icon: <CheckCircleOutlined /> },
};

const StatusTag = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NO_DAY;
  return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
};

export default function BranchDayStatus() {
  const { user } = useAuth();

  const [isHO, setIsHO] = useState(false);
  const [userBranch, setUserBranch] = useState(null);
  const [initLoading, setInitLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setUserBranch(branch);
        setIsHO(branch?.branchType === 'HO' || isGlobalViewer(user));
      } catch {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        businessDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      };
      const res = await fetchDayStatus(params);
      const data = res?.data;
      setRows(data?.data || []);
      setCounts(data?.counts || {});
    } catch (err) {
      setError(err.message || 'Failed to load day status');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!initLoading) fetchStatus();
  }, [selectedDate, initLoading]);

  // ── HO table columns ───────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.branch?.name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.branch?.code}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'dayStatus',
      width: 170,
      render: (v) => <StatusTag status={v} />,
      filters: Object.entries(STATUS_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (value, row) => row.dayStatus === value,
    },
    {
      title: 'Opening Cash',
      dataIndex: ['summary', 'openingCash'],
      width: 130,
      align: 'right',
      render: (v) => v > 0 ? `₹${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Cash In',
      dataIndex: ['summary', 'totalCashIn'],
      width: 120,
      align: 'right',
      render: (v) => v > 0
        ? <Text style={{ color: '#389e0d' }}>₹{fmt(v)}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Cash Out',
      dataIndex: ['summary', 'totalCashOut'],
      width: 120,
      align: 'right',
      render: (v) => v > 0
        ? <Text style={{ color: '#cf1322' }}>₹{fmt(v)}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Closing Balance',
      dataIndex: ['summary', 'closingBalance'],
      width: 140,
      align: 'right',
      render: (v, row) => {
        if (row.dayStatus === 'NO_DAY') return <Text type="secondary">—</Text>;
        return (
          <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>
            ₹{fmt(v)}
          </Text>
        );
      },
    },
    {
      title: 'Physical Cash',
      dataIndex: ['businessDay', 'denominationCount', 'totalPhysicalCash'],
      width: 130,
      align: 'right',
      render: (v, row) => {
        if (!row.businessDay) return <Text type="secondary">—</Text>;
        const phys = row.businessDay?.denominationCount?.totalPhysicalCash || 0;
        return phys > 0 ? `₹${fmt(phys)}` : <Text type="secondary">Not counted</Text>;
      },
    },
    {
      title: 'Difference',
      dataIndex: ['businessDay', 'cashDifference'],
      width: 110,
      align: 'right',
      render: (v, row) => {
        if (!row.businessDay) return <Text type="secondary">—</Text>;
        const diff = row.businessDay?.cashDifference || 0;
        if (diff === 0) return <Text style={{ color: '#389e0d' }}>₹0</Text>;
        return (
          <Text style={{ color: '#cf1322', fontWeight: 500 }}>
            {diff > 0 ? '+' : ''}₹{fmt(Math.abs(diff))}
          </Text>
        );
      },
    },
    {
      title: 'Opened At',
      dataIndex: ['businessDay', 'openedAt'],
      width: 90,
      render: (v) => v ? dayjs(v).format('HH:mm') : <Text type="secondary">—</Text>,
    },
  ];

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const totalBranches = rows.length;
  const openCount = (counts.OPEN || 0) + (counts.REOPENED || 0);
  const pendingCount = (counts.PENDING_CASH_VERIFICATION || 0) + (counts.PENDING_CHECKER_APPROVAL || 0);
  const closedCount = counts.CLOSED || 0;
  const noActivityCount = counts.NO_DAY || 0;

  // ── Branch user single-card view ───────────────────────────────────────────
  if (!isHO && rows.length > 0) {
    const row = rows[0];
    const summary = row.summary;
    const day = row.businessDay;

    return (
      <div style={{ padding: '16px 0' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={5} style={{ margin: 0 }}>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Day Status — {userBranch?.name}
            </Title>
          </Col>
          <Col>
            <Space>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                format="DD/MM/YYYY"
                allowClear={false}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchStatus} loading={loading} />
            </Space>
          </Col>
        </Row>

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

        <Card>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>Business Date</Text>
                <Text strong>{selectedDate?.format('DD MMM YYYY')}</Text>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                <StatusTag status={row.dayStatus} />
              </Space>
            </Col>
          </Row>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Statistic title="Opening Cash" value={fmt(summary.openingCash)} prefix="₹" valueStyle={{ fontSize: 16 }} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title="Cash In" value={fmt(summary.totalCashIn)} prefix="₹" valueStyle={{ color: '#389e0d', fontSize: 16 }} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title="Cash Out" value={fmt(summary.totalCashOut)} prefix="₹" valueStyle={{ color: '#cf1322', fontSize: 16 }} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title="Closing Balance" value={fmt(summary.closingBalance)} prefix="₹" valueStyle={{ fontWeight: 'bold', fontSize: 16 }} />
            </Col>
          </Row>
          {day?.denominationCount?.totalPhysicalCash > 0 && (
            <>
              <Divider />
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Statistic title="Physical Cash" value={fmt(day.denominationCount.totalPhysicalCash)} prefix="₹" valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Difference"
                    value={fmt(Math.abs(day.cashDifference || 0))}
                    prefix={day.cashDifference > 0 ? '+₹' : day.cashDifference < 0 ? '-₹' : '₹'}
                    valueStyle={{ color: day.cashDifference === 0 ? '#389e0d' : '#cf1322', fontSize: 16 }}
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>
      </div>
    );
  }

  // ── HO multi-branch table view ─────────────────────────────────────────────
  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Branch Day Status
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {totalBranches} branch{totalBranches !== 1 ? 'es' : ''}
          </Text>
        </Col>
        <Col>
          <Space>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchStatus} loading={loading} />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable style={{ marginBottom: 16 }} />}

      {/* ── Status summary cards ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ borderColor: '#b7eb8f' }}>
            <Statistic
              title={<Text style={{ color: '#52c41a' }}>Open</Text>}
              value={openCount}
              valueStyle={{ color: '#389e0d', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ borderColor: '#ffd591' }}>
            <Statistic
              title={<Text style={{ color: '#fa8c16' }}>Pending</Text>}
              value={pendingCount}
              valueStyle={{ color: '#fa8c16', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered>
            <Statistic
              title="Closed"
              value={closedCount}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ borderColor: '#d9d9d9' }}>
            <Statistic
              title={<Text type="secondary">No Activity</Text>}
              value={noActivityCount}
              valueStyle={{ color: '#8c8c8c', fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>
      <Divider style={{ margin: '0 0 16px' }} />

      {/* ── Table ── */}
      <Table
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row.branch?._id}
        loading={loading}
        size="small"
        scroll={{ x: 1100 }}
        pagination={false}
        rowClassName={(row) => (row.dayStatus === DAY_STATUS.PENDING_CHECKER_APPROVAL ? 'ant-table-row-pending' : '')}
        locale={{ emptyText: 'No branches found' }}
      />
    </div>
  );
}
